import { components } from "packages/api/generated-schemas/client-portal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Modal,
  BasicButton,
  DangerButton,
  showToast,
} from "@certn/cascadia";
import { useClientPortalSubmit } from "@certn/api";
import { mutate as SWRMutate } from "swr";

interface CancelCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  applicant: string;
  applicantEmail: string;
  status: components["schemas"]["ClientPortalOverallStatus"];
}

export function CancelCaseModal({
  isOpen,
  onClose,
  caseId,
  applicant,
  applicantEmail,
  status,
}: CancelCaseModalProps) {
  const { t } = useTranslation();
  const displayApplicant =
    (applicant || "").trim() || (applicantEmail || "").trim();
  const applicantLabel = displayApplicant || t("Unknown applicant");
  const isPreFulfillment = [
    "APPLICANT_OPENED",
    "APPLICANT_INVITED",
    "APPLICANT_STARTED",
    "CASE_ORDERED",
  ].includes(status.name);

  const { isSubmitting, submit } = useClientPortalSubmit(
    `/api/internal/client-portal/case/${caseId}/cancel-case/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        // Use function-based matcher to handle query parameters in cache keys
        const caseListPaths = [
          "/api/internal/client-portal/case/active/",
          "/api/internal/client-portal/case/review/",
          "/api/internal/client-portal/case/done/",
        ];
        SWRMutate((key) => {
          if (typeof key === "string") {
            return caseListPaths.some((path) => key.startsWith(path));
          }
          return false;
        });

        // Close modal and show feedback immediately
        onClose();
        showToast(t("Case cancelled"), { type: "success" });
      },
    }
  );

  return (
    <Modal testID="cancel-case-modal" isOpen={isOpen}>
      <Dialog>
        <DialogHeader onClose={onClose}>
          {t("Cancel this check: {{applicant}}", {
            applicant: applicantLabel,
          })}
        </DialogHeader>
        <DialogContent>
          <Text style="global/body-primary">
            {isPreFulfillment
              ? t(
                  "This check has not yet entered fulfillment. You can safely cancel it now. This action cannot be undone."
                )
              : t(
                  "Fulfillment in progress This check has already entered fulfillment. You may still be charged if you proceed."
                )}
          </Text>
        </DialogContent>
        <DialogFooter>
          <BasicButton onPress={onClose}>{t("Go Back")}</BasicButton>
          <DangerButton onPress={() => submit()} isLoading={isSubmitting}>
            {t("Cancel check")}
          </DangerButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
